import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from "react-router-dom";
import { useSelector, useDispatch, shallowEqual } from 'react-redux';

// 🆕 Import BasicSEO for authenticated pages
import BasicSEO from '../../Components/SEO/BasicSEO';

import TrustChain from '../../Components/TrustChain/TrustChain';

import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Avatar,
  Chip,
  Button,
  Divider,
  Container,
  Stack,
  LinearProgress,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Skeleton,
  Pagination,
  useMediaQuery,
  useTheme,
  Drawer,
  AppBar,
  Toolbar,
  SwipeableDrawer,
  Collapse,
  Badge,
  IconButton
} from '@mui/material';
import {
  LocationOn,
  Verified,
  ThumbUp,
  Star,
  HowToReg,
  TrendingUp,
  EmojiEvents,
  WorkspacePremium,
  Visibility,
  Edit,
  Message,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  Info as InfoIcon,
  AccountCircle as ProfileIcon,
  Analytics as StatsIcon,
  Business as BusinessIcon,
  School as AchievementsIcon
} from '@mui/icons-material';

import Activity from './Activity';
import { useGetUserProfileQuery } from '../../redux/thunks/users/userprofile';
import { useGetActivitiesQuery } from '../../redux/thunks/users/getactivities';
import UserAchievementBanner from './UserAchievementBanner';
import AchievementsCard from './AchievementsCard';
import NewUserWelcome from './NewUserWelcome';
import NetworkCard from './NetworkCard';
import BusinessSection from './BusinessSection';
import EditProfileDialog from './EditProfileDialog';

const getTrustImpactInfo = (trustScore) => {
  if (trustScore > 70) {
    return {
      level: 'High Impact',
      color: 'success',
      description: 'Carries significant weight in business evaluations and smart ratings'
    };
  } else if (trustScore >= 40) {
    return {
      level: 'Medium Impact',
      color: 'warning',
      description: 'Has moderate influence on platform recommendations'
    };
  } else {
    return {
      level: 'Low Impact',
      color: 'error',
      description: 'Limited influence on platform evaluations'
    };
  }
};

// All existing skeleton components remain exactly the same
const ProfileSkeleton = () => (
  <Card variant="outlined" sx={{ border: 'none' }}>
    <CardContent>
      <Stack spacing={2} alignItems="center">
        <Skeleton variant="circular" width={120} height={120} />
        <Skeleton variant="text" width={150} height={30} />
        <Skeleton variant="text" width={100} height={24} />
        <Stack spacing={1} width="100%">
          <Skeleton variant="text" width="100%" height={24} />
          <Skeleton variant="text" width="100%" height={24} />
          <Skeleton variant="text" width="100%" height={24} />
        </Stack>
      </Stack>
    </CardContent>
  </Card>
);

const ActivitySkeleton = () => (
  <Card variant="outlined" sx={{ border: 'none' }}>
    <CardContent>
      <Skeleton variant="text" width={200} height={32} sx={{ mb: 2 }} />
      <Stack spacing={2}>
        {[1, 2, 3].map((i) => (
          <Box key={i}>
            <Skeleton variant="rectangular" width="100%" height={120} sx={{ borderRadius: 1 }} />
          </Box>
        ))}
      </Stack>
    </CardContent>
  </Card>
);

const StatsSkeleton = () => (
  <Card variant="outlined" sx={{ border: 'none' }}>
    <CardContent>
      <Skeleton variant="text" width={150} height={32} sx={{ mb: 2 }} />
      <Stack spacing={2}>
        <Skeleton variant="circular" width={80} height={80} sx={{ mx: 'auto' }} />
        <Skeleton variant="text" width="100%" height={24} />
        <Skeleton variant="text" width="100%" height={24} />
      </Stack>
    </CardContent>
  </Card>
);

// 🆕 Mobile Profile Sidebar Component
const MobileProfileSidebar = ({ 
  isOpen, 
  onClose,
  userData,
  isLoggedInUser,
  userId,
  handleMessageUser,
  setOpenEditDialog
}) => {
  const [expandedSections, setExpandedSections] = useState({
    profile: true,
    trustScore: false,
    network: false,
    business: false,
    achievements: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const showAdvancedFeatures = false;

  return (
    <SwipeableDrawer
      anchor="right"
      open={isOpen}
      onClose={onClose}
      onOpen={() => {}}
      PaperProps={{
        sx: { 
          width: '100%',
          maxWidth: 400,
          bgcolor: 'background.default'
        }
      }}
    >
      <AppBar position="static" color="default" elevation={0}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Profile Details
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 2, overflow: 'auto', height: '100%' }}>
        {/* Profile Section */}
        <Card sx={{ mb: 2 }}>
          <CardContent 
            sx={{ 
              pb: 1, 
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
            onClick={() => toggleSection('profile')}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ProfileIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">Profile Stats</Typography>
            </Box>
            {expandedSections.profile ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </CardContent>
          <Collapse in={expandedSections.profile}>
            <CardContent sx={{ pt: 0 }}>
              <Stack spacing={2} alignItems="center">
                <Avatar
                  src={userData.profileImageUrl}
                  alt={`${userData.displayName} logo`}
                  sx={{ 
                    width: 80, 
                    height: 80,
                    mx: 'auto', 
                    mb: 1,
                  }}
                >
                  {userData.displayName && userData.displayName.split(' ').map(n => n[0]).join('')}
                </Avatar>
                
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography variant="h6">{userData.displayName}</Typography>
                  {!isLoggedInUser && (
                    <Box onClick={(e) => e.stopPropagation()}>
                      <TrustChain userId={userId} />
                    </Box>
                  )}
                </Box>
                
                {isLoggedInUser ? (
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<Edit />}
                    size="small"
                    onClick={() => {
                      setOpenEditDialog(true);
                      onClose();
                    }}
                    fullWidth
                  >
                    Edit Profile
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Message />}
                    size="small"
                    onClick={() => {
                      handleMessageUser();
                      onClose();
                    }}
                    fullWidth
                  >
                    Message
                  </Button>
                )}
                
                <Divider flexItem />
                <Stack spacing={1} width="100%">
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      <Visibility sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Business Viewed
                    </Typography>
                    <Chip 
                      label={userData.stats?.pageViews?.totalPageViews || 0} 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                    />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      <Star sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Reviews
                    </Typography>
                    <Chip 
                      label={userData.stats?.reviews?.totalReviews || 0} 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                    />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      <HowToReg sx={{ mr: 1, verticalAlign: 'middle' }} />
                      References
                    </Typography>
                    <Chip 
                      label={userData.stats?.referrals?.totalReferrals || 0} 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                    />
                  </Box>
                </Stack>
              </Stack>
            </CardContent>
          </Collapse>
        </Card>

        {/* Trust Score Section */}
        <Card sx={{ mb: 2 }}>
          <CardContent 
            sx={{ 
              pb: 1, 
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
            onClick={() => toggleSection('trustScore')}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <StatsIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">Trust Score</Typography>
              <Chip 
                label={`${(userData.trustScore || 50).toFixed(1)}`}
                size="small" 
                color="primary" 
                sx={{ ml: 1 }}
              />
            </Box>
            {expandedSections.trustScore ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </CardContent>
          <Collapse in={expandedSections.trustScore}>
            <CardContent sx={{ pt: 0 }}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                my: 2 
              }}>
                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                  <CircularProgress
                    variant="determinate"
                    value={userData.trustScore || 50}
                    size={80}
                    thickness={4}
                    sx={{ color: 'primary.main' }}
                  />
                  <Box sx={{
                    top: 0,
                    left: 0,
                    bottom: 0,
                    right: 0,
                    position: 'absolute',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <Typography variant="h6" color="primary.main">
                      {(userData.trustScore || 50).toFixed(1)}
                    </Typography>
                  </Box>
                </Box>
                
                {/* Impact Badge */}
                <Chip 
                  label={getTrustImpactInfo(userData.trustScore || 50).level}
                  color={getTrustImpactInfo(userData.trustScore || 50).color}
                  size="small"
                  sx={{ mt: 1, mb: 2 }}
                />
                
                {/* Impact Description */}
                <Typography 
                  variant="caption" 
                  color="text.secondary" 
                  sx={{ 
                    textAlign: 'center',
                    px: 1,
                    lineHeight: 1.3
                  }}
                >
                  {getTrustImpactInfo(userData.trustScore || 50).description}
                </Typography>
                
                {showAdvancedFeatures && (
                  <>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                      Elite Status
                    </Typography>
                    <Box sx={{ width: '100%', mt: 2 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Monthly Impact
                      </Typography>
                      <LinearProgress 
                        variant="determinate" 
                        value={85} 
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </Box>
                  </>
                )}
              </Box>
            </CardContent>
          </Collapse>
        </Card>

        {/* Network Section */}
        <Card sx={{ mb: 2 }}>
          <CardContent 
            sx={{ 
              pb: 1, 
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
            onClick={() => toggleSection('network')}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ProfileIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">Network</Typography>
              <Badge 
                badgeContent={userData.firstDegreeFriends?.length || 0} 
                color="primary" 
                sx={{ ml: 1 }}
              />
            </Box>
            {expandedSections.network ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </CardContent>
          <Collapse in={expandedSections.network}>
            <CardContent sx={{ pt: 0 }}>
              <NetworkCard userData={userData} />
            </CardContent>
          </Collapse>
        </Card>

        {/* Business Section */}
        <Card sx={{ mb: 2 }}>
          <CardContent 
            sx={{ 
              pb: 1, 
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
            onClick={() => toggleSection('business')}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <BusinessIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">Business</Typography>
              <Badge 
                badgeContent={userData.myBusinesses?.length || 0} 
                color="primary" 
                sx={{ ml: 1 }}
              />
            </Box>
            {expandedSections.business ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </CardContent>
          <Collapse in={expandedSections.business}>
            <CardContent sx={{ pt: 0 }}>
              <BusinessSection userData={userData} />
            </CardContent>
          </Collapse>
        </Card>

        {/* Achievements Section */}
        {userData.tags && userData.tags.length > 0 && (
          <Card sx={{ mb: 2 }}>
            <CardContent 
              sx={{ 
                pb: 1, 
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
              onClick={() => toggleSection('achievements')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <AchievementsIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Achievements</Typography>
                <Badge 
                  badgeContent={userData.tags?.length || 0} 
                  color="primary" 
                  sx={{ ml: 1 }}
                />
              </Box>
              {expandedSections.achievements ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </CardContent>
            <Collapse in={expandedSections.achievements}>
              <CardContent sx={{ pt: 0 }}>
                <AchievementsCard achievements={userData.tags} />
              </CardContent>
            </Collapse>
          </Card>
        )}
      </Box>
    </SwipeableDrawer>
  );
};

// 🆕 Mobile Profile Header Component
const MobileProfileHeader = ({ userData, isLoggedInUser, handleMessageUser, setOpenEditDialog, userId }) => {
  return (
    <Card variant="outlined" sx={{ border: 'none', mb: 3 }}>
      <CardContent>
        <Stack spacing={2} alignItems="center">
          <Avatar
            src={userData.profileImageUrl}
            alt={`${userData.displayName} logo`}
            sx={{ 
              width: 100, 
              height: 100,
              mx: 'auto', 
              mb: 1,
            }}
          >
            {userData.displayName && userData.displayName.split(' ').map(n => n[0]).join('')}
          </Avatar>
          
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="h5">{userData.displayName}</Typography>
            {!isLoggedInUser && (
              <Box onClick={(e) => e.stopPropagation()}>
                <TrustChain userId={userId} />
              </Box>
            )}
          </Box>
          
          {isLoggedInUser ? (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<Edit />}
              onClick={() => setOpenEditDialog(true)}
              size="large"
              sx={{ minWidth: 160 }}
            >
              Edit Profile
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              startIcon={<Message />}
              onClick={handleMessageUser}
              size="large"
              sx={{ minWidth: 160 }}
            >
              Message
            </Button>
          )}
          
          {/* Mobile Quick Stats */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-around', 
            width: '100%',
            mt: 2 
          }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" color="primary">
                {userData.stats?.pageViews?.totalPageViews || 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Business Viewed
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" color="primary">
                {userData.stats?.reviews?.totalReviews || 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Reviews
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" color="primary">
                {userData.stats?.referrals?.totalReferrals || 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                References
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" color="primary">
                {(userData.trustScore || 50).toFixed(1)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Trust Score
              </Typography>
            </Box>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

// All existing component functions remain exactly the same
const TrustScoreCard = ({trustScore}) => {
  const showAdvancedFeatures = false;
  const impactInfo = getTrustImpactInfo(trustScore || 50);

  return (
    <Card variant="outlined" sx={{ border: 'none', mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Trust Score
        </Typography>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          my: 3 
        }}>
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <CircularProgress
              variant="determinate"
              value={trustScore}
              size={80}
              thickness={4}
              sx={{ color: 'primary.main' }}
            />
            <Box sx={{
              top: 0,
              left: 0,
              bottom: 0,
              right: 0,
              position: 'absolute',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <Typography variant="h6" color="primary.main">
                {(trustScore || 50).toFixed(1)}
              </Typography>
            </Box>
          </Box>
          
          {/* Impact Badge */}
          <Chip 
            label={impactInfo.level}
            color={impactInfo.color}
            size="small"
            sx={{ mt: 1, mb: 2 }}
          />
          
          {/* Impact Description */}
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ 
              textAlign: 'center',
              px: 1,
              lineHeight: 1.4
            }}
          >
            {impactInfo.description}
          </Typography>
          
          {showAdvancedFeatures && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Elite Status
            </Typography>
          )}
        </Box>
        
        {showAdvancedFeatures && (
          <Stack spacing={2}>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Monthly Impact
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={85} 
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};
const UserLogo = ({photoUrl, userName}) => {
    
    if (photoUrl) {
      return (
        <Avatar
          src={photoUrl}
          alt={`${userName} logo`}
          sx={{ 
            width: 120, 
            height: 120,
            mx: 'auto', 
            mb: 2,
          }}
        />
      );
    }
    
    return (
      <Avatar 
        sx={{ 
          width: 120, 
          height: 120, 
          mx: 'auto', 
          mb: 2,
          bgcolor: 'grey.300'
        }}
      >
        {userName.split(' ').map(n => n[0]).join('')}
      </Avatar>
    );
  };

// 🆕 Enhanced Main Component with Mobile Support
const ActiveUserProfile = ({}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // Mobile-specific state
    const [sidebarOpen, setSidebarOpen] = useState(false);

    const [isLoading, setIsLoading] = useState(true);
    const [isLoggedInUser, setIsLoggedInUser] = useState(false);
    const [openEditDialog, setOpenEditDialog] = useState(false);

    const token = useSelector((state) => state.auth?.token || null);
    const thisUserId = useSelector((state) => state.auth?.userId || null);

    const { userId } = useParams(); 
    const navigate = useNavigate();

    const [page, setPage] = useState(1);
    const itemsPerPage = 5;

    const { data: userData, isError, isLoading: isUserLoading, error } = useGetUserProfileQuery(userId);

    const activityArgs = { userId: userId, activityType: 'all' };
    const { data: userActivity,  isError: isNetActivityError,  isLoading: isActivityLoading,  error: netActivityError,} = useGetActivitiesQuery(activityArgs);

    // Generate dynamic SEO content for user profiles
    const generateProfileSEO = () => {
      if (isLoading || !userData) {
        return {
          title: "Loading Profile - Revfer",
          description: "Loading user profile information including activity, trust score, and professional network details."
        };
      }

      const userName = userData.displayName || 'User';
      const reviewCount = userData.stats?.reviews?.totalReviews || 0;
      const referralCount = userData.stats?.referrals?.totalReferrals || 0;
      const trustScore = userData.trustScore || 50;
      const activityCount = userActivity?.length || 0;

      if (isLoggedInUser) {
        return {
          title: `My Profile - ${userName} - Revfer`,
          description: `Manage your Revfer profile and activity. Your trust score: ${trustScore.toFixed(1)}, ${reviewCount} reviews written, ${referralCount} businesses referred. View and edit your professional network and business connections.`
        };
      } else {
        return {
          title: `${userName}'s Profile - Revfer`,
          description: `View ${userName}'s professional profile on Revfer. Trust score: ${trustScore.toFixed(1)}, ${reviewCount} reviews, ${referralCount} referrals, ${activityCount} activities. Connect and discover trusted business recommendations.`
        };
      }
    };

    const seoData = generateProfileSEO();

    useEffect(() => {
        if (userData && (thisUserId === userData._id)) {
          setIsLoggedInUser(true);
          return;
        }
    }, [thisUserId, userData]);

    useEffect(() => {
      setIsLoading(isUserLoading || isActivityLoading );
    }, [isUserLoading, isActivityLoading]);

    const handlePageChange = (event, value) => {
      setPage(value);
      window.scrollTo({
        top: document.getElementById('activities-section')?.offsetTop - 20,
        behavior: 'smooth'
      });
    }

    const getPaginatedActivities = () => {
      if (!userActivity) return [];
      const sortedActivities = [...userActivity].sort((a, b) => 
        new Date(b.creationDate) - new Date(a.creationDate)
      );
  
      const startIndex = (page - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      return sortedActivities.slice(startIndex, endIndex);
    };

    const handleMessageUser = () => {
      navigate(`/chat`, {
        state: {
          otherUser: {
            otherUserId: userData._id,
            displayName: userData.displayName,
            photoUrl: userData.profileImageUrl,
            lastMessage: "",
            msgTime: new Date(),
          },
        },
      });
    };

    const totalPages = userActivity ? Math.ceil(userActivity.length / itemsPerPage) : 0;
  
    if (isLoading) {
        return (
          <>
            <BasicSEO 
              title="Loading Profile - Revfer"
              description="Loading user profile information including activity, trust score, and professional network details."
              noIndex={true}
            />
            
            <Box sx={{ bgcolor: 'grey.50', minHeight: '100vh', py: 3 }}>
              <Container maxWidth="lg">
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 1 }} />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Stack spacing={3}>
                      <ProfileSkeleton />
                      <ActivitySkeleton />
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <ActivitySkeleton />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Stack spacing={3}>
                      <StatsSkeleton />
                      <StatsSkeleton />
                    </Stack>
                  </Grid>
                </Grid>
              </Container>
            </Box>
          </>
        );
      }

    return (
      <>
        <BasicSEO 
          title={seoData.title}
          description={seoData.description}
          noIndex={true}
        />

        <Box sx={{ bgcolor: 'grey.50', minHeight: '100vh', py: 3 }}>
          <UserAchievementBanner achievements={userData.tags} />
    
          <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
            {isMobile ? (
              // Mobile Layout - Single Column
              <>
                {/* Mobile Header with Menu */}
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  mb: 3
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {isLoggedInUser ? 'My Profile' : `${userData.displayName}'s Profile`}
                  </Typography>
                  <IconButton 
                    color="primary" 
                    onClick={() => setSidebarOpen(true)}
                    sx={{ 
                      bgcolor: 'primary.light',
                      '&:hover': { bgcolor: 'primary.main', color: 'white' }
                    }}
                  >
                    <MenuIcon />
                  </IconButton>
                </Box>

                {/* Mobile Profile Header */}
                <MobileProfileHeader 
                  userData={userData}
                  isLoggedInUser={isLoggedInUser}
                  handleMessageUser={handleMessageUser}
                  setOpenEditDialog={setOpenEditDialog}
                  userId={userId}
                />

                {/* Mobile Activity Feed - Full Width */}
                <Box id="activities-section">
                  {(!userActivity || userActivity.length <= 1) ? (
                    <NewUserWelcome 
                      userName={userData.displayName}
                      joinDate={userData.creationTime}
                    />
                  ) : (
                    <Card variant="outlined" sx={{ border: 'none', mb: 3 }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Recent Activity
                        </Typography>
                        {getPaginatedActivities().map((activity, index) => (
                          <Activity 
                            key={`${activity.id}-${index}`} 
                            activity={activity} 
                          />
                        ))}
                        {totalPages > 1 && (
                          <Box sx={{ 
                            mt: 3, 
                            display: 'flex', 
                            justifyContent: 'center'
                          }}>
                            <Pagination 
                              count={totalPages}
                              page={page}
                              onChange={handlePageChange}
                              color="primary"
                              size="small"
                              showFirstButton
                              showLastButton
                            />
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  )}
                </Box>

                {/* Mobile Profile Sidebar */}
                <MobileProfileSidebar
                  isOpen={sidebarOpen}
                  onClose={() => setSidebarOpen(false)}
                  userData={userData}
                  isLoggedInUser={isLoggedInUser}
                  userId={userId}
                  handleMessageUser={handleMessageUser}
                  setOpenEditDialog={setOpenEditDialog}
                />
              </>
            ) : (
              // Desktop Layout - Original 3 Columns (unchanged)
              <Grid container spacing={3}>
                <Grid item xs={12} md={3}>
                  <Stack spacing={3}>
                    {/* Profile Card */}
                    <Card variant="outlined" sx={{ border: 'none' }}>
                      <CardContent>
                        <Stack spacing={2} alignItems="center">
                          <UserLogo photoUrl={userData.profileImageUrl} userName={userData.displayName}/>
                          
                          {/* Username with TrustChainIcon */}
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Typography variant="h6">{userData.displayName}</Typography>
                            {/* Only show trust chain icon when not viewing own profile */}
                            {!isLoggedInUser && (
                              <Box onClick={(e) => e.stopPropagation()}>
                                <TrustChain userId={userId} />
                              </Box>
                            )}
                          </Box>
                          
                          {/* Add edit button for logged-in user */}
                          {isLoggedInUser ? (
                            <Button
                              variant="outlined"
                              color="primary"
                              startIcon={<Edit />}
                              size="small"
                              onClick={() => setOpenEditDialog(true)}
                              sx={{ mb: 1 }}
                            >
                              Edit Profile
                            </Button>
                          ) : (
                            <Button
                              variant="contained"
                              color="primary"
                              startIcon={<Message />}
                              size="small"
                              onClick={handleMessageUser}
                              sx={{ mb: 1 }}
                            >
                              Message
                            </Button>
                          )}
                          
                          <Divider flexItem />
                          <Stack spacing={1} width="100%">
                            <Typography variant="body2" color="text.secondary">
                              <Visibility sx={{ mr: 1, verticalAlign: 'middle' }} />
                                {userData.stats?.pageViews?.totalPageViews || 0} Business Viewed
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              <Star sx={{ mr: 1, verticalAlign: 'middle' }} />
                              {userData.stats?.reviews?.totalReviews || 0} Reviews
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              <HowToReg sx={{ mr: 1, verticalAlign: 'middle' }} />
                              {userData.stats?.referrals?.totalReferrals || 0} References
                            </Typography>
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>

                    {/* Expertise Card */}
                    <BusinessSection userData={userData} />
                  </Stack>
                </Grid>

                {/* Middle Column - Activity Feed */}
                <Grid item xs={12} md={6} id="activities-section">
                  {(!userActivity || userActivity.length <= 1) ? (
                    <NewUserWelcome 
                      userName={userData.displayName}
                      joinDate={userData.creationTime}
                    />
                  ) : (
                  <Card variant="outlined" sx={{ border: 'none', mb: 3 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Recent Activity
                      </Typography>
                      {getPaginatedActivities().map((activity, index) => (
                        <Activity 
                          key={`${activity.id}-${index}`} 
                          activity={activity} 
                        />
                      ))}
                      {totalPages > 1 && (
                        <Box sx={{ 
                          mt: 3, 
                          display: 'flex', 
                          justifyContent: 'center'
                        }}>
                          <Pagination 
                            count={totalPages}
                            page={page}
                            onChange={handlePageChange}
                            color="primary"
                            size={isMobile ? "small" : "medium"}
                            showFirstButton
                            showLastButton
                          />
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                )}
              </Grid>
      
                {/* Right Column */}
                <Grid item xs={12} md={3}>
                  <TrustScoreCard trustScore={userData.trustScore} />
                  <NetworkCard userData={userData}/>
                  <AchievementsCard achievements={userData.tags} />
                </Grid>
              </Grid>
            )}
          </Container>
          {isLoggedInUser && (
            <EditProfileDialog 
              open={openEditDialog} 
              onClose={() => setOpenEditDialog(false)} 
            />
          )}
        </Box>
      </>
    );
};
  
export default ActiveUserProfile;